import React from 'react';
import ButtonBase from '@mui/material/ButtonBase';
import Box from '@mui/material/Box';

import { color, r, radius } from '../../theme/tokens';

// RiskChip — medication-interaction risk on a stack row. This and the
// interaction modal are the ONLY places amber and red appear in the product.
//
// Three rules the handoff is emphatic about, all of them load-bearing:
//
//   1. Colour never carries the meaning alone. Every chip has a dot, a text
//      label and a border, so it survives colour blindness and greyscale.
//   2. The chip is absent when there is nothing to flag. Absence IS the
//      signal for a clean check — there is no "Clear" chip (cancelled,
//      STACK-528). A Low-risk finding is a finding we already hold and is
//      shown, on the same terms as Medium. `riskChipLevel` still returns
//      null when `has_interaction` is false, so an errored check with an
//      empty flag list cannot light a chip.
//   3. Medium and low reuse the existing `--caution-*` / `--pos-*` values,
//      which already match the handoff exactly. Only high needed new tokens.
//      Low's dot is `accent` (STACK-528).
//
// Clicking opens the existing InteractionDetailsModal — this phase restyles
// the chip, it does not touch the modal (that is phase 4).

export const RISK_LEVELS = {
  high: {
    label: 'High risk',
    bg: color.riskHighBg,
    fg: color.riskHighInk,
    bd: color.riskHighBorder,
    dot: color.riskHigh,
  },
  medium: {
    label: 'Medium risk',
    bg: color.cautionBg,
    fg: color.cautionInk,
    bd: color.cautionBorder,
    dot: color.riskMedium,
  },
  low: {
    label: 'Low risk',
    bg: color.posBg,
    fg: color.posInk,
    bd: color.posBorder,
    dot: color.accent,
  },
};

/**
 * Which chip a supplement earns, or null for none.
 *
 * Guards on `has_interaction` as well as `risk` because saved stacks predating
 * the interaction checker carry a `risk` string with no actual finding behind
 * it, and those must not light up a chip.
 */
export const riskChipLevel = (supplement) => {
  const interaction = supplement?.interaction;
  if (!interaction || !interaction.has_interaction || !interaction.risk) return null;
  if (typeof interaction.risk !== 'string') return null;
  const level = interaction.risk.toLowerCase();
  return RISK_LEVELS[level] ? level : null;
};

const RiskChip = ({ level, onClick, count }) => {
  const spec = RISK_LEVELS[level];
  if (!spec) return null;

  const detail = count ? ` — ${count} medication ${count === 1 ? 'interaction' : 'interactions'}` : '';

  return (
    <ButtonBase
      onClick={onClick}
      title="Review interactions with your meds"
      aria-label={`${spec.label}${detail}. Review interactions with your medications`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: r(6),
        padding: `${r(6)} ${r(11)}`,
        border: `${r(1)} solid ${spec.bd}`,
        borderRadius: radius.sm,
        backgroundColor: spec.bg,
        color: spec.fg,
        fontSize: r(12),
        fontWeight: 700,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        '&:focus-visible': {
          outline: `${r(2)} solid ${color.accent}`,
          outlineOffset: r(2),
        },
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: r(7),
          height: r(7),
          borderRadius: '50%',
          backgroundColor: spec.dot,
          flex: 'none',
        }}
      />
      {spec.label} →
    </ButtonBase>
  );
};

export default RiskChip;
